import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { NavLink, Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import './App.css';
import { GameProvider } from './GameContext';
import Admin from './components/Admin';
import Battlefield from './components/Battlefield';
import LoadingWrapper from './components/LoadingWrapper';
import MyArmy from './components/MyArmy';
import UnitManager from './components/UnitManager';
import { setTokenProvider } from './services/api';

const BROADCASTER_USERNAME = process.env.REACT_APP_BROADCASTER_USERNAME || '';

function App() {
  const { user, isAuthenticated, loginWithRedirect, logout, getAccessTokenSilently } = useAuth0();

  // Set up token provider for API calls
  useEffect(() => {
    setTokenProvider(async () => {
      try {
        if (isAuthenticated) {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            }
          });
          return token;
        }
        return '';
      } catch (err) {
        console.error('Error getting access token:', err);
        return '';
      }
    });
  }, [isAuthenticated, getAccessTokenSilently]);


  // Helper function to get the actual Twitch username
  const getTwitchUsername = (): string => {
    if (!user) return '';

    // Priority: custom claims from Actions > built-in fields
    return (user as any)['https://raid.nlith.it/user_name']     // From our Action
      || (user as any)['https://raid.nlith.it/username']        // Alternative from Action
      || (user as any)['https://twitch.tv/username']            // From old Action
      || (user as any).user_name                                // Built-in (usually not present)
      || user.nickname                                          // Fallback
      || user.name                                              // Last resort
      || '';
  };

  const isBroadcaster = (): boolean => {
    if (!isAuthenticated || !user) return false;

    const twitchUsername = getTwitchUsername();
    return twitchUsername.toLowerCase() === BROADCASTER_USERNAME.toLowerCase();
  };

  // Protected route component for broadcaster-only pages
  const BroadcasterRoute = ({ children }: { children: React.ReactElement }) => {
    if (!isBroadcaster()) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2>Access Denied</h2>
          <p>Only the broadcaster can access this page.</p>
        </div>
      );
    }
    return children;
  };

  return (
    <GameProvider>
      <Router>
        <div className="App">
          <nav className="navigation">
            <div className="nav-left">
              <NavLink to="/" end>
                {({ isActive }) => (
                  <button className={isActive ? 'active' : ''}>Battlefield View</button>
                )}
              </NavLink>
              {isBroadcaster() && (
                <>
                  <NavLink to="/admin">
                    {({ isActive }) => (
                      <button className={isActive ? 'active' : ''}>Admin Area</button>
                    )}
                  </NavLink>
                  <NavLink to="/unit-manager">
                    {({ isActive }) => (
                      <button className={isActive ? 'active' : ''}>Unit Manager</button>
                    )}
                  </NavLink>
                </>
              )}
              {isAuthenticated && !isBroadcaster() && (
                <NavLink to="/my-army">
                  {({ isActive }) => (
                    <button className={isActive ? 'active' : ''}>My Army</button>
                  )}
                </NavLink>
              )}
              {/* <NavLink to="/auth-debug">
                {({ isActive }) => (
                  <button className={isActive ? 'active debug-btn' : 'debug-btn'}>🔍 Auth Debug</button>
                )}
              </NavLink> */}
            </div>
            <div className="nav-right">
              {!isAuthenticated ? (
                <button className="login-btn" onClick={() => loginWithRedirect()}>
                  Login with Twitch
                </button>
              ) : (
                <div className="user-info">
                  <span className="username">
                    {getTwitchUsername() || 'User'}
                    {isBroadcaster() && <span className="broadcaster-badge">Broadcaster</span>}
                  </span>
                  <button className="logout-btn" onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </nav>

          <div className="content">
            <LoadingWrapper>
              <Routes>
                <Route path="/" element={<Battlefield />} />
                <Route path="/admin" element={<BroadcasterRoute><Admin /></BroadcasterRoute>} />
                <Route path="/unit-manager" element={<BroadcasterRoute><UnitManager /></BroadcasterRoute>} />
                <Route path="/my-army" element={<MyArmy />} />
                {/* <Route path="/auth-debug" element={<AuthDebug />} /> */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </LoadingWrapper>
          </div>
        </div>
      </Router>
    </GameProvider>
  );
}

export default App;
